import React, {createContext, useContext} from 'react';

import type {QuestionComponentProps} from '../shared/QuestionComponent/QuestionComponent.react';

import type {FrqGrading} from './frqGradingStatus';
import IncompletenessBanner from './IncompletenessBanner';
import SubmittedFrqStatus from './SubmittedFrqStatus.react';
import ErrorBoundary from './WrittenSubmission/ErrorBoundary';
import WrittenSubmission from './WrittenSubmission/WrittenSubmission.react';

export interface FreeResponseInputAreaValue {
  activeQuestion: QuestionComponentProps['activeQuestion'];
  storeName?: string;
  guess?: QuestionComponentProps['guess'];
  questionState: QuestionComponentProps['questionState'];
  /** The question's rubric, as `question.getMeta().getQuestionRubric()` returns it. */
  questionRubric?: unknown;
  isViewingStudentWork?: boolean;
  frqGrading?: FrqGrading;
}

const FreeResponseInputAreaContext = createContext<FreeResponseInputAreaValue | null>(null);

export const FreeResponseInputAreaProvider = FreeResponseInputAreaContext.Provider;

/**
 * What a free response question puts in the answer-input slot: the response editor, preceded by the
 * submission's grading status once there is a guess to report on.
 *
 * Must stay at module scope and take its inputs from context. `QuestionComponent` renders this as a
 * component type, so one built during a render is a new type every render — which remounts the
 * response editor, and the editor blanks itself for 100ms on mount.
 */
export function FreeResponseInputArea() {
  const value = useContext(FreeResponseInputAreaContext);

  if (!value || value.questionState === 'Dennis') {
    return null;
  }

  const {
    activeQuestion,
    storeName,
    guess,
    questionState,
    questionRubric,
    isViewingStudentWork,
    frqGrading
  } = value;

  let writtenSubmissionFragment: React.ReactNode = null;
  if (questionState === 'PreGuess') {
    writtenSubmissionFragment = (
      <WrittenSubmission
        storeName={storeName}
        question={activeQuestion}
        submitted={!!guess?.getId()}
        isViewingStudentWork={isViewingStudentWork}
      />
    );
  } else if (guess?.getId()) {
    writtenSubmissionFragment = (
      <>
        <SubmittedFrqStatus
          questionId={activeQuestion?.getId?.()}
          rubric={questionRubric}
          grading={frqGrading}
          isViewingStudentWork={isViewingStudentWork}
        />
        <WrittenSubmission
          storeName={storeName}
          question={activeQuestion}
          submitted
          isViewingStudentWork={isViewingStudentWork}
        />
      </>
    );
  } else {
    writtenSubmissionFragment = <IncompletenessBanner />;
  }

  return <ErrorBoundary>{writtenSubmissionFragment}</ErrorBoundary>;
}
